import React from 'react';
import {
  Dialog,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Alert,
  Divider,
  List,
  ListItemButton,
  ListItemText,
} from '@mui/material';
import { BuffOrDebuff, Spell, WordEffect } from '../../types';
import { useWarbandStore } from '../../stores/warbandStore';
import { useDirtyTrackingStore } from '../../stores/dirtyTrackingStore';
import { useReferenceDataStore } from '../../stores/referenceDataStore';
import { formatBuffEffect, getBuffName } from '../../utils/formatting';
import { SelectSpellDialog } from '../shared/SelectSpellDialog';

const WORD_BUFF_IDS: ReadonlySet<string> = new Set(['spell_power_word', 'spell_draining_word']);

interface WarbandBuffsModalProps {
  open: boolean;
  onClose: () => void;
}

export const WarbandBuffsModal: React.FC<WarbandBuffsModalProps> = ({ open, onClose }) => {
  const { spellcastersAndSpells, warbandBuffs, allAvailableSpells, wordEffects } = useWarbandStore();
  const allBuffs = useReferenceDataStore((state) => state.buffs);
  const updateWarbandBuffs = useDirtyTrackingStore((s) => s.updateWarbandBuffs);
  const updateWordEffects = useDirtyTrackingStore((s) => s.updateWordEffects);

  const [error, setError] = React.useState<string | null>(null);
  const [selectedBuffId, setSelectedBuffId] = React.useState<string | null>(null);
  const [pendingWordBuffId, setPendingWordBuffId] = React.useState<string | null>(null);

  const warbandSpells: Spell[] = React.useMemo(
    () => spellcastersAndSpells?.warbandSpells ?? [],
    [spellcastersAndSpells?.warbandSpells]
  );
  const warbandSpellIds = React.useMemo(
    () => new Set(warbandSpells.map((s) => s.id)),
    [warbandSpells]
  );

  React.useEffect(() => {
    if (!open) return;
    setSelectedBuffId(null);
    setError(null);
  }, [open]);

  const handleSave = () => {
    if (!selectedBuffId) return;
    // Word spell buffs need a target spell — open the selector instead of saving directly
    if (WORD_BUFF_IDS.has(selectedBuffId)) {
      setPendingWordBuffId(selectedBuffId);
      return;
    }
    // Expand existing stacks back to repeated IDs then append the new one
    const existingIds = warbandBuffs.flatMap((b) => Array(b.stackCount ?? 1).fill(b.id));
    updateWarbandBuffs([...existingIds, selectedBuffId]);
    onClose();
  };

  const handleTargetSelected = (targetSpell: Spell) => {
    if (!pendingWordBuffId) return;
    const buffId = pendingWordBuffId as WordEffect['buffId'];
    // Save the buff
    const existingIds = warbandBuffs.flatMap((b) => Array(b.stackCount ?? 1).fill(b.id));
    updateWarbandBuffs([...existingIds, buffId]);
    // Save the word effect target, replacing any previous target for this buff
    const newEffect: WordEffect = { buffId, targetSpellId: targetSpell.id, targetSpellName: targetSpell.name };
    updateWordEffects([...wordEffects.filter((e) => e.buffId !== buffId), newEffect]);
    setPendingWordBuffId(null);
    onClose();
  };

  // Build a map of current stack counts per buff ID
  const activeBuffCounts = new Map<string, number>();
  for (const b of warbandBuffs) {
    activeBuffCounts.set(b.id, b.stackCount ?? 1);
  }

  // Only show warband-level buffs (globalYou > 0 || globalThem > 0), filtered by stacking limit
  const availableBuffs = allBuffs.filter((b: BuffOrDebuff) => {
    if (b.globalYou <= 0 && b.globalThem <= 0) return false;
    const currentCount = activeBuffCounts.get(b.id) ?? 0;
    if (currentCount === 0) return true;
    if (b.stacking === 0) return false;
    return currentCount < b.stacking;
  });

  const warbandSpellBuffs = availableBuffs.filter(
    (b) => b.source === 'spell' && b.sourceId != null && warbandSpellIds.has(b.sourceId)
  );
  const otherSpellBuffs = availableBuffs.filter(
    (b) => b.source === 'spell' && b.sourceId != null && !warbandSpellIds.has(b.sourceId)
  );
  const customBuffs = availableBuffs.filter((buff) => buff.source === 'custom' || buff.isCustom);

  return (
    <>
      <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth PaperProps={{ sx: { height: 400 } }}>
        <DialogContent dividers sx={{ overflow: 'auto' }}>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          {warbandSpellBuffs.length === 0 && otherSpellBuffs.length === 0 && customBuffs.length === 0 ? (
            <Typography variant="body2" color="textSecondary">
              No warband-level effects available to add.
            </Typography>
          ) : (
            <Box>
              {warbandSpellBuffs.length > 0 && (
                <>
                  <Typography variant="caption" color="textSecondary" sx={{ display: 'block', mb: 0.5 }}>
                    From Your Spells
                  </Typography>
                  <List dense disablePadding>
                    {warbandSpellBuffs.map((buff) => (
                      <ListItemButton
                        key={buff.id}
                        selected={selectedBuffId === buff.id}
                        onClick={() => setSelectedBuffId(buff.id)}
                        sx={{ borderRadius: 1, py: 0.5 }}
                      >
                        <ListItemText
                          primary={getBuffName(buff)}
                          secondary={formatBuffEffect(buff)}
                          primaryTypographyProps={{ variant: 'body2' }}
                          secondaryTypographyProps={{ variant: 'caption' }}
                        />
                      </ListItemButton>
                    ))}
                  </List>
                </>
              )}

              {otherSpellBuffs.length > 0 && (
                <>
                  {warbandSpellBuffs.length > 0 && <Divider sx={{ my: 1 }} />}
                  <Typography variant="caption" color="textSecondary" sx={{ display: 'block', mb: 0.5 }}>
                    Other Spells
                  </Typography>
                  <List dense disablePadding>
                    {otherSpellBuffs.map((buff) => (
                      <ListItemButton
                        key={buff.id}
                        selected={selectedBuffId === buff.id}
                        onClick={() => setSelectedBuffId(buff.id)}
                        sx={{ borderRadius: 1, py: 0.5 }}
                      >
                        <ListItemText
                          primary={getBuffName(buff)}
                          secondary={formatBuffEffect(buff)}
                          primaryTypographyProps={{ variant: 'body2' }}
                          secondaryTypographyProps={{ variant: 'caption' }}
                        />
                      </ListItemButton>
                    ))}
                  </List>
                </>
              )}
              {customBuffs.length > 0 && (
                <>
                  {(warbandSpellBuffs.length > 0 || otherSpellBuffs.length > 0) && <Divider sx={{ my: 1 }} />}
                  <Typography variant="caption" color="textSecondary" sx={{ display: 'block', mb: 0.5 }}>
                    Custom Effects
                  </Typography>
                  <List dense disablePadding>
                    {customBuffs.map((buff) => (
                      <ListItemButton
                        key={buff.id}
                        selected={selectedBuffId === buff.id}
                        onClick={() => setSelectedBuffId(buff.id)}
                        sx={{ borderRadius: 1, py: 0.5 }}
                      >
                        <ListItemText
                          primary={getBuffName(buff)}
                          secondary={formatBuffEffect(buff)}
                          primaryTypographyProps={{ variant: 'body2' }}
                          secondaryTypographyProps={{ variant: 'caption' }}
                        />
                      </ListItemButton>
                    ))}
                  </List>
                </>
              )}
            </Box>
          )}
        </DialogContent>

        <DialogActions>
          <Button onClick={onClose}>Cancel</Button>
          <Button variant="contained" onClick={handleSave} disabled={selectedBuffId === null}>
            {selectedBuffId && WORD_BUFF_IDS.has(selectedBuffId) ? 'Next: Select Target Spell' : 'Add Warband Effect'}
          </Button>
        </DialogActions>
      </Dialog>

      <SelectSpellDialog
        open={pendingWordBuffId !== null}
        title="Select Target Spell"
        spells={allAvailableSpells}
        onSelect={handleTargetSelected}
        onClose={() => setPendingWordBuffId(null)}
      />
    </>
  );
};
